import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	If,
	Image,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import {
	permissions as allPermissions,
	ClassYearUtilities,
	Icon,
	StaticURLs,
	StudentEnrollmentIntentComponents,
	StudentUtilities,
	useElementSize,
	useStudentsWithEnrollmentIntentsListQuery,
} from "@hex-insights/verita-romania.shared";
import styles from "./styles.module.css";

const requiredPermissions: string[] = [];

export type ReEnrollmentQuestionnaireNoticePermissionRequiredProps = Partial<ChildrenProps>;

export function ReEnrollmentQuestionnaireNoticePermissionRequired({
	children,
}: ReEnrollmentQuestionnaireNoticePermissionRequiredProps) {
	return (
		<PermissionRequired
			requiredPermissions={requiredPermissions}
			customCheck={checkReEnrollmentPermissions}
			checkMethod="and"
			customCheckMethod="and"
		>
			{children}
		</PermissionRequired>
	);
}

function checkReEnrollmentPermissions(permissions: Record<string, boolean>) {
	const hasViewPermission =
		permissions[allPermissions.StudentEnrollmentIntent.View.Admin] ||
		permissions[allPermissions.StudentEnrollmentIntent.View.Basic];
	const hasCreatePermission =
		permissions[allPermissions.StudentEnrollmentIntent.Create.Admin] ||
		permissions[allPermissions.StudentEnrollmentIntent.Create.Basic];
	const hasUpdatePermission =
		permissions[allPermissions.StudentEnrollmentIntent.Update.Admin] ||
		permissions[allPermissions.StudentEnrollmentIntent.Update.Basic];
	return hasViewPermission && hasCreatePermission && hasUpdatePermission;
}

export type ReEnrollmentQuestionnaireNoticeProps = Partial<ClassNameProps & StyleProps>;

export function ReEnrollmentQuestionnaireNotice({ className, style }: ReEnrollmentQuestionnaireNoticeProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const today = toLocalDateString(new Date());
	const { loading, data } = useStudentsWithEnrollmentIntentsListQuery({
		variables: {
			filters: {
				studentAcademicTermEnrollments: [
					{
						academicTerm: [
							{
								startDateLTE: today,
								endDateGTE: today,
							},
						],
						earlyEndDateIsNull: true,
					},
				],
				relationships: [
					{
						guardian: [
							{
								person: [
									{
										user: [
											{
												isCurrentEQ: true,
											},
										],
									},
								],
							},
						],
					},
				],
			},
			studentEnrollmentIntentFilters: {
				academicYear: [{ nameEQ: "2024-2025" }], // TODO dynamic academic year selection
			},
		},
	});
	const studentIDs = data?.studentConnection.edges.map(({ node }) => node.id) ?? [];
	const anyIncomplete =
		data?.studentConnection.edges.some(({ node }) => node.studentEnrollmentIntents.length === 0) ?? true;

	const { loading: loadingClassYears, classYears } = ClassYearUtilities.useClassYearsMap();
	const allStudentsGraduating = data?.studentConnection.edges.every(({ node }) => {
		const nextClassYear = StudentUtilities.getNextClassYear(node, classYears);
		return !nextClassYear;
	});

	const shouldNotRender = (loading && !data) || loadingClassYears || allStudentsGraduating;

	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const { width } = useElementSize(containerRef, shouldNotRender);
	const isNarrow = width < 600;

	if (shouldNotRender) {
		return null;
	}

	return (
		<React.Fragment>
			<div ref={containerRef} onClick={openModal}>
				<Conditional>
					<If condition={anyIncomplete}>
						<Row
							justify="space-between"
							align="center"
							className={makeClassName(
								styles["re-enrollment-notice"],
								styles["re-enrollment-notice--incomplete"],
								isNarrow ? styles["re-enrollment-notice--narrow"] : "",
								className,
							)}
							style={{ rowGap: "1rem", ...style }}
						>
							<Column justify="spaced-start" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
								<Column justify="spaced-start" verticalSpacing="0.5rem">
									<Row justify="space-between" align="flex-start" style={{ columnGap: "1rem" }}>
										<span className={styles["re-enrollment-notice__heading"]}>2024-2025 Enrollment Now&nbsp;Open!</span>
										<If condition={isNarrow}>
											<Icon.Star size={36} weight="fill" color="var(--brand-colors--primary--dark)" />
										</If>
									</Row>
									<span className={styles["re-enrollment-notice__message"]}>
										Please click the button below to secure your {studentIDs.length === 1 ? "student's" : "students'"}{" "}
										enrollment status for next&nbsp;year.
									</span>
								</Column>
								<Button variant="primary" onClick={openModal}>
									Enroll Students
								</Button>
							</Column>

							<If condition={!isNarrow}>
								<Image
									src={StaticURLs.Images.schoolScenery1}
									alt="School Scenery"
									width={205}
									height={135}
									sizing="width-scaled"
									className={styles["re-enrollment-notice__image"]}
								/>
							</If>
						</Row>
					</If>
					<Else>
						<Row
							justify="space-between"
							align="center"
							className={makeClassName(
								styles["re-enrollment-notice"],
								styles["re-enrollment-notice--complete"],
								isNarrow ? styles["re-enrollment-notice--narrow"] : "",
								className,
							)}
							style={{ rowGap: "1rem", ...style }}
						>
							<Column justify="spaced-start" verticalSpacing="0.5rem" style={{ flexGrow: 1 }}>
								<Row justify="space-between" align="flex-start" style={{ columnGap: "1rem" }}>
									<span className={styles["re-enrollment-notice__heading"]}>2024-2025 Enrollment Complete!</span>
									<If condition={isNarrow}>
										<Icon.CheckCircle size={24} color="var(--theme-colors__green)" />
									</If>
								</Row>
								<Button variant="link" size="small" onClick={openModal}>
									View Enrollment Details
								</Button>
							</Column>

							<If condition={!isNarrow}>
								<Icon.CheckCircle size={57} color="var(--theme-colors__green)" />
							</If>
						</Row>
					</Else>
				</Conditional>
			</div>

			<Modal.If condition={isModalOpen}>
				<StudentEnrollmentIntentComponents.StudentEnrollmentIntentStudentsReenrollmentModal
					studentIDs={studentIDs}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
